/* eslint-disable consistent-return */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { isArray, isString } from 'lodash';
import { Answer } from '@/models/Answer';
import { Question as QuestionModel, Option } from '@/models/Question';
import { Component, Vue, Prop, Emit } from 'vue-property-decorator';
import { Report } from '@/models/Report';
import { AxiosError } from 'axios';
import { DateFormatter } from '@/support/DateFormatter';
import { DateTime } from 'luxon';
import { ValidationRules, Rules, Rule } from '@/support/ValidationRules';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';

@Component<Question>({
  name: 'question',
})
export default class Question extends Vue {
  @Prop()
  protected question!: QuestionModel;

  @Prop({ default: false })
  protected disabled!: boolean;

  @Prop()
  protected answers!: Answer[] | {[key: string]: string};

  @Prop()
  protected report!: Report;

  @Prop()
  protected emitOnChange!: boolean;

  protected inputValue = '';

  protected inputValueCheckbox = '';

  protected refreshChildren = false;

  protected inputValueRadio: Option = {};

  protected date: DateFormatter = new DateFormatter();

  protected validationRules: Rules = ValidationRules();

  protected currencyMask = createNumberMask({
    prefix: '',
    allowDecimal: true,
    thousandsSeparatorSymbol: '.',
    decimalSymbol: ',',
    allowNegative: false,
  });

  @Emit()
  protected overwriteAssignmentValue(): string {
    return '';
  }

  protected mounted(): void {
    this.setAnswers();
    if (this.$refs.quillEditor) {
      (this.$refs.quillEditor as any).quill.enable(! this.disabled);
    }
  }

  protected setAnswers() {
    if (! this.answer) {
      return;
    }

    if (this.question.type_raw === 'date') {
      return this.fillDateInput();
    }

    if (this.question.type_raw === 'checkbox') {
      return this.fillCheckboxInput();
    }

    return this.fillInputValue();
  }

  protected fillInputValue() {
    this.inputValue = this.answerToString();
  }

  protected fillCheckboxInput() {
    const answerModelValue = (this.answer as Answer).value;

    if (answerModelValue) {
      return this.inputValueCheckbox = answerModelValue;
    }

    this.inputValueCheckbox = this.answer as string;
  }

  protected fillDateInput() {
    const answer = this.answerToString();

    if (answer && answer === 'Invalid DateTime') {
      return;
    }

    this.date.selectedDate = DateTime.fromSQL(answer).toFormat('yyyy-MM-dd');
  }

  protected answerToString() {
    const answerModelValue = (this.answer as Answer).value;
    if (answerModelValue !== undefined) {
      return answerModelValue as string;
    }

    return this.answer as string;
  }

  protected fillRadioInput() {
    if (! this.question.options) { return; }

    const answerModelValue = (this.answer as Answer).value;
    this.question.options.forEach((option: Option) => {
      if (answerModelValue && (option.value === answerModelValue)) {
        return this.inputValueRadio = option;
      }

      if (option.value === this.answer) {
        return this.inputValueRadio = option;
      }
    });
  }

  protected textareaChanged(value: string) {
    this.inputValue = value;
    this.questionAnswered();
  }

  protected getRules(key: string): Rule[] {
    if (key === 'applicant_phone') {
      return [this.validationRules.lengthMinimum10];
    }

    if (key === 'applicant_email') {
      return [this.validationRules.email];
    }

    return [];
  }

  protected questionAnswered(key?: string, value?: string) {
    if (this.report) {
      if (key === 'besluit_aan_bestuur') {
        this.$emit('besluitBestuurUpdated', value);
      }

      if (key && key === 'applicant_phone') {
        if (isString(this.validationRules.lengthMinimum10(`${value}`))) {
          return;
        }
      }

      if (key && key === 'applicant_email') {
        if (isString(this.validationRules.email(`${value}`))) {
          return;
        }
      }

      if (this.emitOnChange) {
        return;
      }

      return this.saveReportAnswer();
    }
  }

  protected async saveReportAnswer() {
    const payload = {
      value: this.answerValuePayload,
      hin_report: this.report.uuid,
      question: this.question.uuid,
    };

    this.refreshChildren = true;
    const answer = await new Answer()
      .create(payload)
      .catch((error: AxiosError) => {
        console.log('error', error);
      });
    this.refreshChildren = false;

    this.$emit('reportAnswerSaved', answer);
  }

  protected isVisibleDeleteButton(option: Option) {
    if (! this.inputValueRadio || ! option) {
      return false;
    }

    return option.value === this.inputValueRadio.value;
  }

  protected get answerValuePayload() {
    if (this.question.type_raw === 'radio') {
      return this.inputValueRadio.value;
    }

    if (this.question.type_raw === 'checkbox') {
      return this.inputValueCheckbox;
    }

    if (this.question.type_raw === 'date' && this.date.selectedDate) {
      return DateTime.fromSQL(this.date.selectedDate).toFormat('yyyy-LL-dd');
    }

    if (this.question.type_raw === 'currency' && this.question.key !== 'current_value') {
      const formattedValue = this.inputValue;
      const float = Number(formattedValue.replaceAll('.', '').replaceAll(',', '.'));

      return float;
    }

    return this.inputValue;
  }

  protected isSelectedCheckbox(option: Option): boolean {
    return this.inputValueCheckbox === option.uuid;
  }

  protected getAnswerForQuestion(question: QuestionModel) {
    if (isArray(this.answers)) {
      return this.getAnswerFromAnswerModel(question);
    }

    return this.getAnswerFromObject(question) as string;
  }

  protected getAnswerFromAnswerModel(question: QuestionModel) {
    const answer = (this.answers as Answer[]).find((indexedAnswer: Answer) => indexedAnswer.question && indexedAnswer.question.key === question.key);

    return answer || null;
  }

  protected getAnswerFromObject(question: QuestionModel) {
    if (! question || ! question.key || ! this.answers) { return; }

    if (! (this.answers as {[key: string]: string})[question.key]) {
      return '';
    }
    const answer = (this.answers as {[key: string]: string})[question.key];

    return answer || null;
  }

  protected get answer() {
    return this.getAnswerForQuestion(this.question);
  }

  protected get isDisabled(): boolean {
    if (typeof this.question.is_disabled === 'string') {
      return this.question.is_disabled === 'true';
    }

    return !! this.question.is_disabled;
  }

  @Emit()
  protected emitAnswerPayload(): Record<string, string> {
    return {
      value: `${this.answerValuePayload}`,
      question: `${this.question.uuid}`,
      hin_report: `${this.report.uuid}`,
    };
  }
}
